import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  CircularProgress,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Email, Business } from '@material-ui/icons';
import { toast } from 'react-toastify';
import api from '../../services/api';
import useAuth from "../../hooks/useAuth.js";

const useStyles = makeStyles((theme) => ({
  emailSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  infoBox: {
    padding: theme.spacing(2),
    background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    border: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? '1px solid rgba(255,255,255,0.15)'
      : '1px solid #bbdefb',
    borderLeft: `4px solid ${theme.palette.info ? theme.palette.info.main : theme.palette.primary.main}`,
  },
  infoBoxTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: (theme.palette.info ? theme.palette.info.main : theme.palette.primary.main),
    display: 'flex',
    alignItems: 'center'
  },
  emailPreviewTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  emailPreviewBox: {
    padding: 16,
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? 'rgba(255,255,255,0.05)'
      : '#f5f5f5',
    borderRadius: 8,
    fontFamily: 'Arial, sans-serif',
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`
  },
  emailPreviewSignatureDivider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 12,
    marginTop: 8
  }
}));

const EmailPersonalization = () => {
  const classes = useStyles();
  const { user } = useAuth();
  
  const [emailConfig, setEmailConfig] = useState({
    emailFromName: '',
    emailSignature: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Carrega configurações da empresa atual
  useEffect(() => {
    loadEmailPersonalization();
  }, []);

  const loadEmailPersonalization = async () => {
    try {
      setLoading(true);
      // Busca as configurações específicas da empresa atual
      const fromNameResponse = await api.get('/companySettingOne/', { 
        params: { column: 'emailFromName' }
      });
      const signatureResponse = await api.get('/companySettingOne/', { 
        params: { column: 'emailSignature' }
      });
      
      setEmailConfig({
        emailFromName: fromNameResponse.data?.data || '',
        emailSignature: signatureResponse.data?.data || ''
      });
    } catch (error) {
      console.error('Erro ao carregar personalização de email:', error);
      toast.error('Erro ao carregar configurações de email');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setEmailConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSaveSetting = async (field, value) => {
    try {
      await api.put('/companySettings/', {
        column: field,
        data: value
      });
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      throw error;
    }
  };

  const handleSaveAllSettings = async () => {
    try {
      setLoading(true);
      
      // Salva todas as configurações de personalização
      await handleSaveSetting('emailFromName', emailConfig.emailFromName);
      await handleSaveSetting('emailSignature', emailConfig.emailSignature);
      
      toast.success('Personalização de email salva com sucesso!');
      setHasChanges(false);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações de email');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !hasChanges) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className={classes.emailSection}>
      <Typography variant="h5" className={classes.sectionTitle}>
        <Email />
        Personalização de Emails
      </Typography>
      
      <Box className={classes.infoBox}>
        <Typography variant="body2" className={classes.infoBoxTitle}>
          ℹ️ <strong>Informações importantes:</strong>
        </Typography>
        <Typography variant="body2" color="textSecondary">
          • Os emails são enviados através do servidor da administração do sistema<br/>
          • Você pode personalizar apenas o <strong>nome do remetente</strong> e a <strong>assinatura</strong><br/>
          • O endereço de email será sempre o configurado pela administração<br/>
          • Essas configurações se aplicam a todos os emails enviados pela sua empresa
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nome do Remetente"
            placeholder={`Sistema ${user?.company?.name || 'Minha Empresa'}`}
            value={emailConfig.emailFromName}
            onChange={(e) => handleConfigChange('emailFromName', e.target.value)}
            className={classes.formField}
            helperText="Nome que aparecerá nos emails enviados pela sua empresa"
            InputProps={{
              startAdornment: (
                <Business color="action" style={{ marginRight: 8 }} />
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Assinatura dos Emails"
            placeholder={`Atenciosamente,\nEquipe ${user?.company?.name || 'Minha Empresa'}\n\nTelefone: (11) 99999-9999\nEmail: contato@minhaempresa.com\nSite: www.minhaempresa.com`}
            value={emailConfig.emailSignature}
            onChange={(e) => handleConfigChange('emailSignature', e.target.value)}
            className={classes.formField}
            helperText="Assinatura personalizada que será adicionada aos emails da sua empresa"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider style={{ margin: '16px 0' }} />
          
          <Typography variant="subtitle2" className={classes.emailPreviewTitle}>
            <strong>Preview do Email:</strong>
          </Typography>
          <Box className={classes.emailPreviewBox}>
            <div style={{ marginBottom: 12 }}>
              <strong>De:</strong> "{emailConfig.emailFromName || `Sistema ${user?.company?.name}`}" &lt;sistema@administracao.com&gt;
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Para:</strong> usuario@exemplo.com
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Assunto:</strong> Bem-vindo ao sistema!
            </div>
            
            <Divider style={{ margin: '12px 0' }} />
            
            <div style={{ marginBottom: 16 }}>
              Olá João,<br/><br/>
              Seja bem-vindo ao nosso sistema!<br/>
              Seu cadastro foi realizado com sucesso.
            </div>
            
            {emailConfig.emailSignature && (
              <div className={classes.emailPreviewSignatureDivider}>
                {emailConfig.emailSignature.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAllSettings}
            disabled={!hasChanges || loading}
            className={classes.saveButton}
          >
            {loading ? <CircularProgress size={20} /> : 'Salvar Personalização'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EmailPersonalization;
